import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

// Admin-only store for generated CV versions. Generation itself lives in
// the cv_generation_runs store: a run is queued, and when it succeeds that
// store refetches this list and selects the new draft.

export interface CvApproachPoint {
  heading: string
  detail: string
}

export interface CvRole {
  title: string
  employer: string
  location: string
  start: string
  end: string
  bullets: string[]
}

export interface CvBoardRole {
  title: string
  organisation: string
  description: string
  start: string
  end: string
  bullets: string[]
}

export interface CvProject {
  name: string
  url: string
  period: string
  description: string
  bullets: string[]
}

export interface CvEducation {
  degree: string
  institution: string
  start: string
  end: string
  details: string[]
}

export interface CvSkillGroup {
  category: string
  items: string[]
}

export interface CvLanguage {
  language: string
  level: string
}

export interface CvDocument {
  summary: string
  approach: CvApproachPoint[]
  roles: CvRole[]
  board_roles: CvBoardRole[]
  projects: CvProject[]
  education: CvEducation[]
  skills: CvSkillGroup[]
  languages: CvLanguage[]
}

// Fixed closing line the public page prints in place of a courses section.
export const CV_COURSES_NOTE = 'Courses and certifications: available on request'

export type CvVersionStatus = 'draft' | 'approved' | 'superseded'

export interface CvCitation {
  path: string
  title: string
}

export interface CvVersion {
  id: number
  status: CvVersionStatus
  data: CvDocument
  citations: CvCitation[]
  model: string | null
  input_tokens: number | null
  output_tokens: number | null
  generated_at: string | null
  approved_at: string | null
  created_at: string
}

export const useCvVersionsStore = defineStore('cvVersions', () => {
  const {
    data: versions,
    loaded,
    loading,
    error: loadError,
    refresh: fetchVersions,
  } = useAsyncResource<CvVersion[]>(
    async () => {
      const res = await fetch('/api/v1/cv_versions', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load CV versions: ${res.status}`)
      return (await res.json()).versions ?? []
    },
    { initialValue: [] },
  )

  const selectedId = ref<number | null>(null)

  const selected = computed(() => versions.value.find((v) => v.id === selectedId.value) ?? null)

  function select(id: number | null): void {
    selectedId.value = id
  }

  function replace(updated: CvVersion): void {
    versions.value = versions.value.map((v) => (v.id === updated.id ? updated : v))
  }

  async function updateDraft(id: number, data: CvDocument): Promise<CvVersion> {
    const updated = await apiMutate<CvVersion>(`/api/v1/cv_versions/${id}`, {
      method: 'PATCH',
      body: { cv_version: { data } },
      failureLabel: 'failed to save the draft',
    })
    replace(updated)
    return updated
  }

  // The server supersedes the previously approved version in the same
  // transaction; mirror that locally so the list needs no refetch.
  async function approve(id: number): Promise<CvVersion> {
    const approved = await apiMutate<CvVersion>(`/api/v1/cv_versions/${id}/approve`, {
      method: 'POST',
      failureLabel: 'failed to approve the draft',
    })
    versions.value = versions.value.map((v) => {
      if (v.id === approved.id) return approved
      return v.status === 'approved' ? { ...v, status: 'superseded' } : v
    })
    return approved
  }

  return {
    versions,
    loaded,
    loading,
    loadError,
    fetchVersions,
    selectedId,
    selected,
    select,
    updateDraft,
    approve,
  }
})
